import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

// 🔧 扩展 Axios 配置类型，添加自定义属性
declare module 'axios' {
  export interface AxiosRequestConfig {
    isAuth?: boolean // 是否需要认证，默认为true
    isToken?: boolean // 兼容旧代码
    isEncrypt?: boolean // 是否加密
  }
  
  export interface InternalAxiosRequestConfig {
    isAuth?: boolean
    isToken?: boolean
    isEncrypt?: boolean
  }
}

import { ElMessage, ElMessageBox, ElNotification } from 'element-plus'
import qs from 'qs'
import { config } from '@/config/axios/config'
import {
  getAccessToken,
  getRefreshToken,
  getTenantId,
  getVisitTenantId,
  removeToken,
  setToken
} from '@/utils/auth'
import errorCode from './errorCode'

import { resetRouter } from '@/router/index'
import { deleteUserCache } from '@/hooks/web/useCache'
import { ApiEncrypt } from '@/utils/encrypt'
import { useEnterpriseStore } from '@/stores/enterprise'

const tenantEnable = import.meta.env.VITE_APP_TENANT_ENABLE
const { result_code, base_url, request_timeout } = config

// 需要忽略的提示。忽略后，自动 Promise.reject('error')
const ignoreMsgs = [
  '无效的刷新令牌', // 刷新令牌被删除时，不用提示
  '刷新令牌已过期', // 使用刷新令牌，刷新获取新的访问令牌时，结果因为过期失败，此时需要忽略。否则，会导致继续 401，无法跳转到登出界面
  '您没有权限查看此模板' // 创建模板后立即获取时可能触发，静默处理
]
// 是否显示重新登录
export const isRelogin = { show: false }
// Axios 无感知刷新令牌，参考 https://www.dashingdog.cn/article/11 与 https://segmentfault.com/a/1190000020210980 实现
// 请求队列
let requestList: any[] = []
// 是否正在刷新中
let isRefreshToken = false
// 请求白名单，无须 token 的接口
const whiteList: string[] = ['/login', '/refresh-token']

// 创建axios实例
const service: AxiosInstance = axios.create({
  baseURL: base_url, // api 的 base_url
  timeout: request_timeout, // 请求超时时间
  withCredentials: false, // 禁用 Cookie 等信息
  // 自定义参数序列化函数
  paramsSerializer: (params) => {
    return qs.stringify(params, { allowDots: true })
  }
})

// request拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 🔧 修复 URL 拼接问题：确保以 / 开头的路径正确拼接 /portal 前缀
    // axios 默认行为：当 URL 以 / 开头时，会忽略 baseURL 的路径部分
    // 这里手动处理，确保路径正确
    // 排除已有前缀的路径：/portal、/app-api、/admin-api
    if (config.url && config.url.startsWith('/') && 
        !config.url.startsWith('/portal') && 
        !config.url.startsWith('/app-api') && 
        !config.url.startsWith('/admin-api')) {
      const apiPrefix = import.meta.env.VITE_API_URL || '/portal'
      config.url = apiPrefix + config.url
    }
    
    // 🔧 移动端 H5 WebView 场景：优先使用 Seal-Token
    const urlParams = new URLSearchParams(window.location.search)
    const sealToken = urlParams.get('token')
    const mode = urlParams.get('mode')
    
    // 如果在移动端 WebView 环境且有 Seal-Token，则使用它
    // 支持所有移动端模式：sign(签署), view(查看), create(创建), form(填写), seal-create(创建印章), template-preview(模板预览)
    if (sealToken && mode) {
      config.headers['Seal-Token'] = sealToken
      console.log('[Axios] 🔐 使用 Seal-Token 认证（移动端 H5 场景，mode:', mode, '）')
    } else {
      // 标准场景：使用 Authorization Token
      // 是否需要设置 token
      let isToken = (config!.headers || {}).isToken === false
      whiteList.some((v) => {
        if (config.url && config.url.indexOf(v) > -1) {
          return (isToken = false)
        }
      })
      if (getAccessToken() && !isToken) {
        config.headers.Authorization = 'Bearer ' + getAccessToken() // 让每个请求携带自定义token
        console.log('[Axios] 已添加 Authorization token:', getAccessToken()?.substring(0, 20) + '...')
      } else if (!getAccessToken() && !isToken) {
        console.warn('[Axios] 需要认证但未找到 token, URL:', config.url)
      }
    }
    // 设置租户
    if (tenantEnable && tenantEnable === 'true') {
      // 优先从缓存获取租户ID，如果没有则使用环境变量中的默认租户ID
      let tenantId = getTenantId()
      if (!tenantId) {
        const defaultTenantId = import.meta.env.VITE_TENANT_ID || '1'
        tenantId = Number(defaultTenantId)
      }
      config.headers['tenant-id'] = tenantId
      
      // 只有登录时，才设置 visit-tenant-id 访问租户
      const visitTenantId = getVisitTenantId()
      if (config.headers.Authorization && visitTenantId) {
        config.headers['visit-tenant-id'] = visitTenantId
      }
    }
    const method = config.method?.toUpperCase()
    // 防止 GET 请求缓存
    if (method === 'GET') {
      config.headers['Cache-Control'] = 'no-cache'
      config.headers['Pragma'] = 'no-cache'
    }
    // 自定义参数序列化函数
    else if (method === 'POST') {
      const contentType = config.headers['Content-Type'] || config.headers['content-type']
      if (contentType === 'application/x-www-form-urlencoded') {
        if (config.data && typeof config.data !== 'string') {
          config.data = qs.stringify(config.data)
        }
      }
    }
    // 是否 API 加密
    if ((config!.headers || {}).isEncrypt) {
      try {
        // 加密请求数据
        if (config.data) {
          config.data = ApiEncrypt.encryptRequest(config.data)
          // 设置加密标识头
          config.headers[ApiEncrypt.getEncryptHeader()] = 'true'
        }
      } catch (error) {
        console.error('请求数据加密失败:', error)
        throw error
      }
    }
    
    // 🔥🔥🔥 添加企业上下文信息（用于权限控制）
    try {
      const enterpriseStore = useEnterpriseStore()
      
      console.error('🔥🔥🔥 [Axios拦截器执行] URL:', config.url)
      
      if (!enterpriseStore.isPersonalIdentity && enterpriseStore.currentEnterprise && config.headers) {
        // 设置企业ID（仅企业身份时才发送）
        config.headers['X-Enterprise-Id'] = String(enterpriseStore.currentEnterprise.id)
        // 设置角色代码
        if (enterpriseStore.currentEnterprise.role) {
          config.headers['X-Role-Code'] = String(enterpriseStore.currentEnterprise.role)
        }
        
        if (config.url?.includes('sign-task')) {
          console.warn('🔥🔥🔥 [AXIOS] 任务请求:', {
            url: config.url,
            enterpriseId: enterpriseStore.currentEnterprise.id,
            roleCode: enterpriseStore.currentEnterprise.role
          })
        }
      } else {
        // 🔧 修复：个人身份可以不带企业ID，只打印调试日志，不报错
        if (config.url?.includes('sign-task')) {
          console.log('[AXIOS] 任务请求未设置企业ID（个人身份模式）:', config.url)
        }
      }
    } catch (error) {
      console.error('❌ Enterprise store error in axios:', error)
    }
    
    return config
  },
  (error: AxiosError) => {
    // Do something with request error
    console.log(error) // for debug
    return Promise.reject(error)
  }
)

// response 拦截器
service.interceptors.response.use(
  async (response: AxiosResponse<any>) => {
    let { data } = response
    const config = response.config
    if (!data) {
      // 返回“[HTTP]请求没有返回值”;
      throw new Error()
    }

    // 检查是否需要解密响应数据
    const encryptHeader = ApiEncrypt.getEncryptHeader()
    const isEncryptResponse =
      response.headers[encryptHeader] === 'true' ||
      response.headers[encryptHeader.toLowerCase()] === 'true'
    if (isEncryptResponse && typeof data === 'string') {
      try {
        // 解密响应数据
        data = ApiEncrypt.decryptResponse(data)
      } catch (error) {
        console.error('响应数据解密失败:', error)
        throw new Error('响应数据解密失败: ' + (error as Error).message)
      }
    }

    // 未设置状态码则默认成功状态
    // 二进制数据则直接返回，例如说 Excel 导出
    if (
      response.request.responseType === 'blob' ||
      response.request.responseType === 'arraybuffer'
    ) {
      // 注意：如果导出的响应为 json，说明可能失败了，不直接返回进行下载
      if (response.data.type !== 'application/json') {
        return response.data
      }
      data = await new Response(response.data).json()
    }
    const code = data.code || result_code
    // 获取错误信息
    const msg = data.msg || (errorCode as any)[code] || errorCode['default']
    if (ignoreMsgs.indexOf(msg) !== -1) {
      // 如果是忽略的错误码，直接返回 msg 异常
      return Promise.reject(msg)
    }
    
    // 🔧 关键修复：检查是否为 token 错误（包括业务错误码 1012005005 "无效的访问令牌"）
    const isTokenError = code === 401 || 
      code === 1012005005 || 
      msg?.includes('无效的访问令牌') || 
      msg?.includes('访问令牌') ||
      (msg?.includes('token') && (msg?.includes('invalid') || msg?.includes('expired') || msg?.includes('过期') || msg?.includes('无效')))
    
    if (isTokenError) {
      console.log('[Service] Token 错误检测:', { code, msg, url: config.url, hasRetry: (config as any)._retry })
      
      // 🔧 关键修复：如果是 Seal-Token 错误（业务错误码 1012005005），且已经重试过，直接停止循环
      if ((config as any)._retry) {
        console.error('[Service] ❌ Token 错误且已重试过，停止循环，直接登出')
        return handleAuthorized()
      }
      
      // 🔧 特殊处理：如果是刚登录后的 getUserInfo 请求失败，不要立即跳转登录
      // 这可能是因为 token 还没有完全同步到请求中
      if (config.url?.includes('/api/v1/member/user/get')) {
        console.warn('[Service] ⚠️ 获取用户信息失败，可能是 token 未同步，建议检查登录流程')
      }
      
      // 🔧 关键修复：在登录后不应该立即尝试刷新 token
      // 如果是登录或用户信息接口返回401，说明是真正的认证失败，不应该尝试刷新
      const isAuthEndpoint = config.url?.includes('/auth/login') || 
                            config.url?.includes('/auth/sms-login') ||
                            config.url?.includes('/member/user/get')
      
      if (isAuthEndpoint) {
        console.error('[Service] 🚫 认证端点返回401，直接登出，不尝试刷新')
        return handleAuthorized()
      }
      
      // 如果未认证，并且未进行刷新令牌，说明可能是访问令牌过期了
      if (!isRefreshToken) {
        isRefreshToken = true
        // 1. 如果获取不到刷新令牌，则只能执行登出操作
        if (!getRefreshToken()) {
          console.warn('[Service] 无 refreshToken，直接登出')
          isRefreshToken = false
          return handleAuthorized()
        }
        // 2. 进行刷新访问令牌
        try {
          console.log('[Service] 开始刷新 Token...', { refreshToken: getRefreshToken()?.substring(0, 20) + '...' })
          const refreshTokenRes = await refreshToken()
          console.log('[Service] 刷新响应:', refreshTokenRes)
          
          // 2.1 刷新成功，则回放队列的请求 + 当前请求
          // 🔧 关键修复：检查响应数据结构
          if (refreshTokenRes && refreshTokenRes.data && refreshTokenRes.data.data) {
            setToken(refreshTokenRes.data.data)
            config.headers!.Authorization = 'Bearer ' + getAccessToken()
            // 🔧 标记为重试请求，防止无限循环
            ;(config as any)._retry = true
            console.log('[Service] Token 刷新成功，重试请求')
            requestList.forEach((cb: any) => {
              cb()
            })
            requestList = []
            isRefreshToken = false
            return service(config)
          } else {
            console.error('[Service] ❌ 刷新响应数据格式错误:', refreshTokenRes)
            throw new Error('刷新响应数据格式错误')
          }
        } catch (e) {
          // 为什么需要 catch 异常呢？刷新失败时，请求因为 Promise.reject 触发异常。
          // 🔧 修复：刷新失败后，清空队列而不是回放，避免死循环
          console.error('[Service] ❌ 刷新 Token 失败，清空请求队列，跳转登录', e)
          requestList = [] // 清空队列，不再回放
          isRefreshToken = false
          return handleAuthorized()
        }
      } else {
        // 添加到队列，等待刷新获取到新的令牌
        console.log('[Service] 添加到刷新队列:', config.url)
        return new Promise((resolve) => {
          requestList.push(() => {
            config.headers!.Authorization = 'Bearer ' + getAccessToken() // 让每个请求携带自定义token 请根据实际情况自行修改
            resolve(service(config))
          })
        })
      }
    } else if (code === 403) {
      // License 授权错误处理
      // 检查是否为 Seal 模块授权错误（后端返回的 data 中包含 contact 信息）
      if (data.data && data.data.contact) {
        const licenseInfo = data.data
        const contact = licenseInfo.contact || {}
        
        // 显示友好的授权提示弹窗
        ElMessageBox.alert(
          `<div style="text-align: center; padding: 10px 0;">
            <div style="margin-bottom: 15px;">
              <i class="el-icon" style="font-size: 48px; color: #E6A23C;">
                <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" width="48" height="48">
                  <path fill="currentColor" d="M224 448a32 32 0 0 0-32 32v384a32 32 0 0 0 32 32h576a32 32 0 0 0 32-32V480a32 32 0 0 0-32-32H224zm0-64h576a96 96 0 0 1 96 96v384a96 96 0 0 1-96 96H224a96 96 0 0 1-96-96V480a96 96 0 0 1 96-96z"/>
                  <path fill="currentColor" d="M512 544a32 32 0 0 1 32 32v192a32 32 0 1 1-64 0V576a32 32 0 0 1 32-32zm192-160v-64a192 192 0 1 0-384 0v64h384zM512 64a256 256 0 0 1 256 256v128H256V320A256 256 0 0 1 512 64z"/>
                </svg>
              </i>
            </div>
            <div style="font-size: 16px; color: #E6A23C; font-weight: bold; margin-bottom: 15px;">
              ${licenseInfo.statusDesc || '授权验证失败'}
            </div>
            <div style="color: #666; line-height: 2; text-align: left; background: #f5f7fa; padding: 15px; border-radius: 8px;">
              <div style="margin-bottom: 10px;"><strong>如需获取授权，请联系：</strong></div>
              <div>邮　箱：<a href="mailto:${contact.email || 'shawn@leepm.com'}" style="color: #409EFF;">${contact.email || 'shawn@leepm.com'}</a></div>
              <div>公众号：<span style="color: #409EFF;">${contact.wechat || '小华同学ai'}</span>（微信搜索关注）</div>
            </div>
            <div style="color: #999; font-size: 12px; margin-top: 15px;">
              ${licenseInfo.tip || '获取授权后，请将 License 文件放置于项目根目录并重启服务'}
            </div>
          </div>`,
          'Seal 电子签章模块授权提示',
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: '我知道了',
            center: true,
            customClass: 'license-alert-dialog'
          }
        )
      } else {
        // 普通 403 权限错误
        ElMessage.error(msg || '当前操作没有权限')
      }
      return Promise.reject(new Error(msg))
    } else if (code === 500) {
      // 显示后端返回的具体错误消息，而不是通用的"服务器内部错误"
      ElMessage.error(msg || '服务器内部错误')
      return Promise.reject(new Error(msg))
    } else if (code === 901) {
      ElMessage.error({
        offset: 300,
        dangerouslyUseHTMLString: true,
        message:
          '<div>' +
          '演示环境不能操作' +
          '</div>' +
          '<div> &nbsp; </div>' +
          '<div>参考 https://doc.iocoder.cn/ 教程</div>' +
          '<div> &nbsp; </div>' +
          '<div>5 分钟搭建本地环境</div>'
      })
      return Promise.reject(new Error(msg))
    } else if (code !== 200) {
      if (msg === '无效的刷新令牌') {
        // hard coding：忽略这个提示，直接登出
        console.log(msg)
        return handleAuthorized()
      } else {
        ElNotification.error({ title: msg })
      }
      return Promise.reject('error')
    } else {
      return data
    }
  },
  (error: AxiosError) => {
    console.log('err' + error) // for debug
    let { message } = error
    if (message === 'Network Error') {
      message = '后端接口连接异常'
    } else if (message.includes('timeout')) {
      message = '系统接口请求超时'
    } else if (message.includes('Request failed with status code')) {
      message = '系统接口发生异常 ' + message.substr(message.length - 3)
    }
    ElMessage.error(message)
    return Promise.reject(error)
  }
)

const refreshToken = async () => {
  axios.defaults.headers.common['tenant-id'] = getTenantId()
  return await axios.post(base_url + '/system/auth/refresh-token?refreshToken=' + getRefreshToken())
}
const handleAuthorized = () => {
  if (!isRelogin.show) {
    // 如果已经到登录页面则不进行弹窗提示
    if (window.location.href.includes('login')) {
      return
    }
    isRelogin.show = true
    ElMessageBox.confirm('登录状态已过期，您可以继续留在该页面，或者重新登录', '系统提示', {
      showCancelButton: false,
      closeOnClickModal: false,
      showClose: false,
      closeOnPressEscape: false,
      confirmButtonText: '重新登录',
      type: 'warning',
      draggable: true,
      center: true,
      customClass: 'switch-confirm-dialog' // Reuse the global style from EnterpriseSwitcher
    }).then(() => {
      resetRouter() // 重置静态路由表
      deleteUserCache() // 删除用户缓存
      removeToken()
      isRelogin.show = false
      // 干掉token后再走一次路由让它过router.beforeEach的校验
      window.location.href = window.location.href
    })
  }
  return Promise.reject('登录状态已过期')
}
export { service }
